import { useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'

import useDebouncedCallback from '@shared/hooks/src/useDebouncedCallback'
import usePubSub, { PubSubEvents } from '@shared/hooks/src/usePubSub'

export default function AnimatedBackground() {
  const theme = useTheme()

  const dimensions = useMemo(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1920
    const h = typeof window !== 'undefined' ? window.innerHeight : 1080
    return { width: w, height: h }
  }, [])

  const blobs = useMemo(() => {
    const { width, height } = dimensions
    const minDimension = Math.min(width, height)

    // Read from `secondary` so a white label partner's colour carries through here too
    const { main, dark, light } = theme.palette.secondary
    const plum = theme.palette.brand.purple

    return [
      // Top left corner
      { id: 'blob-1', colors: [main, plum], initialX: width * -0.05, initialY: height * -0.05, size: minDimension * 0.35 },
      // Top right corner
      { id: 'blob-2', colors: [light, main], initialX: width * 0.75, initialY: height * -0.1, size: minDimension * 0.32 },
      // Middle left
      { id: 'blob-3', colors: [plum, dark], initialX: width * -0.1, initialY: height * 0.4, size: minDimension * 0.38 },
      // Middle right
      { id: 'blob-4', colors: [main, light], initialX: width * 0.7, initialY: height * 0.35, size: minDimension * 0.36 },
      // Bottom left
      { id: 'blob-5', colors: [dark, main], initialX: width * 0.1, initialY: height * 0.7, size: minDimension * 0.34 },
      // Bottom right
      { id: 'blob-6', colors: [plum, light], initialX: width * 0.65, initialY: height * 0.75, size: minDimension * 0.3 },
    ]
  }, [dimensions, theme.palette])

  return (
    <Box sx={styles.backgroundLayer} aria-hidden="true">
      <Box sx={styles.blobsContainer}>
        {blobs.map((blob) => (
          <AnimatedBlob key={blob.id} blob={blob} width={dimensions.width} height={dimensions.height} />
        ))}
      </Box>
      <Box sx={styles.blurOverlay} />
    </Box>
  )
}

function AnimatedBlob({ blob, width, height }) {
  const [position, setPosition] = useState({ x: blob.initialX, y: blob.initialY })

  const shuffle = useDebouncedCallback(() => {
    setPosition(generateRandomPosition(width, height, blob.size))
  }, 500)

  usePubSub(PubSubEvents.AnimatedBackgroundShuffle, shuffle)

  return (
    <Box
      sx={{
        position: 'absolute',
        width: blob.size,
        height: blob.size,
        borderRadius: '50%',
        opacity: 0.14,
        background: `linear-gradient(135deg, ${blob.colors[0]} 0%, ${blob.colors[1]} 100%)`,
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'transform',
      }}
    />
  )
}

function generateRandomPosition(maxWidth, maxHeight, size) {
  const halfSize = size / 2
  return {
    x: Math.random() * (maxWidth + halfSize) - halfSize,
    y: Math.random() * (maxHeight + halfSize) - halfSize,
  }
}

const styles = {
  backgroundLayer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    backgroundColor: 'background.default',
    pointerEvents: 'none',
    zIndex: -1,
  },
  blobsContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  blurOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backdropFilter: 'blur(120px)',
    WebkitBackdropFilter: 'blur(120px)',
    pointerEvents: 'none',
  },
}
